import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { useSignerContracts } from './useWallet';
import type { MethaContracts } from '../lib/contracts';
import { pushToast, updateToast } from '../lib/toast';

type ContractName = keyof MethaContracts;

export type WriteStatus = 'idle' | 'submitting' | 'pending' | 'success' | 'error';

export type WriteResult = {
  hash: string;
  receipt: ethers.TransactionReceipt;
};

export type WriteOptions = {
  /** ETH value to send with the call, in wei. */
  value?: bigint;
  /** Toast title for the pending state. */
  pendingTitle?: string;
  /** Toast title for success. */
  successTitle?: string;
  /** Queries to invalidate on success (matched by the leading key segments). */
  invalidate?: Array<readonly unknown[]>;
};

/**
 * Generic wrapper for contract writes. Returns a `write()` function plus
 * status/error state. Submits via the signer-bound contract, waits for the
 * receipt, posts a 3-step toast (pending → success/error), and invalidates
 * any matching TanStack Query keys on success.
 *
 * @example
 *   const { write, status } = useWriteContract();
 *   await write({
 *     contract: 'registry',
 *     fn: 'registerFirm',
 *     args: [name, juris, level, cats, stake],
 *     pendingTitle: 'Registering firm…',
 *     successTitle: 'Firm registered',
 *     invalidate: [['metha', 'registry']],
 *   });
 */
export function useWriteContract() {
  const contracts = useSignerContracts();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<WriteStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const write = useCallback(
    async (params: {
      contract: ContractName;
      fn: string;
      args?: unknown[];
      options?: WriteOptions;
    }): Promise<WriteResult | null> => {
      if (!contracts) {
        const e = new Error('Wallet not connected');
        setError(e);
        pushToast('error', 'Wallet not connected', { message: 'Connect your wallet to send a transaction.' });
        return null;
      }
      const { contract, fn, args = [], options = {} } = params;
      const c = contracts[contract] as ethers.Contract;
      const method = c[fn];
      if (typeof method !== 'function') {
        const e = new Error(`Contract '${contract}' has no function '${fn}'`);
        setError(e);
        pushToast('error', 'Bad call', { message: e.message });
        return null;
      }

      setStatus('submitting');
      setError(null);
      const pendingTitle = options.pendingTitle ?? `${contract}.${fn}…`;
      const successTitle = options.successTitle ?? `${contract}.${fn} confirmed`;
      const toastId = pushToast('pending', pendingTitle, { message: 'Sign the transaction in your wallet.' });

      try {
        const overrides = options.value !== undefined ? { value: options.value } : {};
        const txArgs = overrides.value !== undefined ? [...args, overrides] : args;
        const tx = (await method(...txArgs)) as ethers.ContractTransactionResponse;

        setStatus('pending');
        updateToast(toastId, {
          kind: 'pending',
          title: pendingTitle,
          message: 'Waiting for confirmation…',
          txHash: tx.hash,
        });

        const receipt = await tx.wait();
        if (!receipt) {
          throw new Error('Transaction dropped from the mempool');
        }
        setStatus('success');
        updateToast(toastId, {
          kind: 'success',
          title: successTitle,
          message: `Block ${receipt.blockNumber}`,
          txHash: tx.hash,
        });

        // Invalidate matching cache keys so reads refresh.
        if (options.invalidate) {
          for (const key of options.invalidate) {
            queryClient.invalidateQueries({ queryKey: key as readonly unknown[] });
          }
        } else {
          // Sensible default: invalidate the contract's whole namespace.
          queryClient.invalidateQueries({ queryKey: ['metha', contract] });
        }

        return { hash: tx.hash, receipt };
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setStatus('error');
        setError(e);
        updateToast(toastId, {
          kind: 'error',
          title: 'Transaction failed',
          message: decodeRevertReason(e),
        });
        return null;
      }
    },
    [contracts, queryClient]
  );

  return { write, status, error, reset: () => { setStatus('idle'); setError(null); } };
}

/** Pluck the human-readable reason out of an ethers v6 error if available. */
function decodeRevertReason(err: Error): string {
  // ethers v6 attaches `reason`, `shortMessage`, and `info.error.data` on CallExceptions.
  // We cast through `unknown` first because TS won't directly bridge Error → Record<...>
  // (no index signature overlap) — the runtime values are present, we just need to peek.
  type Anyish = Record<string, unknown>;
  const e = err as unknown as Anyish;
  const reason = e.reason as string | undefined;
  if (reason) return reason;
  const shortMessage = e.shortMessage as string | undefined;
  if (shortMessage) return shortMessage;
  // User-rejected
  if ((e.code as string | number | undefined) === 4001 || /user.*reject|user.*denied/i.test(err.message)) {
    return 'You rejected the transaction in your wallet.';
  }
  return err.message;
}
